import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { map, slice } from "lodash";
import { useDebounce } from "react-use";
import styled, { css, keyframes } from "styled-components";
import {
  Elevation,
  FontWeight,
  Radius,
  Spacing,
  Transition,
  ZIndexes,
  background,
  content,
  transparentize,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import { usePages, useSize } from "@lkf/hooks";
import Button from "@ui-kit/button";
import Icon from "@ui-kit/icon";
import Tooltip from "@ui-kit/tooltip";
import Picture from "@ui-kit/utils/image";

type Item = {
  id: string;
  src: string; // URL of picture source
  mediaFormat: "picture" | "video"; // From which media format the thumbnail comes from
  srcFallback?: string; // URL of fallback picture (used when src fails to load)
  href: string; // URL of targeted link
  data?;
};

export interface IProps {
  items: Item[];
  renderTooltip: (data: Item["data"]) => React.ReactNode;
  locale: Locale;
  timezone: Timezone;
  className?: string;
  buttonLabel: string;
  onClick?: () => void;
}

const getItemUID = (item: Item) => {
  if (!item.id) {
    return null;
  }
  const mediaFormat = item.mediaFormat ?? "picture";
  return item.id + "_" + mediaFormat;
};

const onEnter = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Root = styled.div`
  position: relative;
  height: 100%;
`;

const Body = styled.div`
  position: absolute;
  top: 0;
  bottom: 38px;
  left: 0;
  right: 0;
`;

const Wrapper = styled.div<{ initialized: boolean }>`
  width: 100%;
  position: absolute;
  overflow-y: hidden;
  will-change: height;
  opacity: 0;
  ${({ initialized }) =>
    initialized &&
    css`
      animation: ${onEnter} ${Transition.Length} ease forwards;
    `}
`;

const Pics = styled.div`
  width: 100%;
  transition: transform 0.7s;
  will-change: transform;
`;

const PicPosition = styled.div`
  position: absolute;
  will-change: width, height, top, left;
`;

const PicWrapper = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${Radius.Small};
  overflow: hidden;
  background: ${(props) => transparentize(0.5, content(props, "tertiary"))};
  color: ${(props) => content(props, "secondary")};
  cursor: pointer;
  filter: brightness(95%);
`;

const Footer = styled.footer`
  display: flex;
  align-items: center;
  flex-direction: row-reverse;
  align-content: flex-start;
  justify-content: space-between;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`;

const appear = keyframes`
  from {
    padding: 0;
    border-width: 0;
    box-shadow: ${Elevation.Elevation_1};
  }
  to {
    padding: 6px;
    margin-left: -18px;
    margin-top: -18px;
    border-width: ${Spacing.Medium};
    box-shadow: ${Elevation.Elevation_3};
  }
`;

const HighlightedImage = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${ZIndexes.AboveContent};
  border-radius: ${Radius.Small};
  background-color: ${(props) => content(props, "tertiary")};
  background-size: cover;
  background-position: center;
  box-sizing: content-box;
  pointer-events: none;
  cursor: pointer;
  animation: ${appear} ${Transition.Length} ease forwards;
  border-color: ${background};
  border-style: solid;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => content(props, "secondary")};
  font-weight: ${FontWeight.Normal};
`;

const Counter = styled.div`
  margin-left: ${Spacing.Medium};
`;

type HightlightProps = {
  image: string;
  left: number;
  top: number;
  size: number;
};
const Highlight = ({ image, left, top, size }: HightlightProps) => {
  const [error, setError] = useState(false);
  const picture = new Image();
  picture.src = image;
  picture.onerror = () => setError(true);
  return (
    <HighlightedImage
      style={{
        left,
        top,
        width: size,
        height: size,
        backgroundImage: `url(${image})`,
      }}
    >
      {error && <Icon glyph="image" size={28} color="red" />}
    </HighlightedImage>
  );
};

const PictureLoader = ({
  imageSize,
  item,
  onHoverImage,
  setInitialized,
}: {
  imageSize: number;
  item: Item;
  onHoverImage: (image: string) => void;
  setInitialized: (isInitialized: boolean) => void;
}) => {
  const [loadedImage, setLoadedImage] = useState(null);
  const onLoad = useCallback(
    (image) => {
      setLoadedImage(image);
      setInitialized(true);
    },
    [setInitialized]
  );
  return (
    <PicWrapper
      onMouseEnter={() => onHoverImage(loadedImage)}
      onMouseLeave={() => onHoverImage(null)}
      style={{ width: imageSize, height: imageSize }}
      href={item.href}
      target="_blank"
    >
      <Picture
        image={item.src}
        fallbackImage={item.srcFallback}
        mediaFormat={item.mediaFormat}
        lazy
        animated
        onLoad={onLoad}
      />
    </PicWrapper>
  );
};

const PictureWall = React.memo(
  ({ items, renderTooltip, buttonLabel, onClick, className }: IProps) => {
    const [scroll, setScroll] = useState(0);
    const [initialized, setInitialized] = useState(false);
    const [hovered, setHovered] = useState(null);
    const [displayLimit, setDisplayLimit] = useState(0);
    const ref = useRef();
    const { width, height } = useSize(ref);
    const [widthDebounced, setWidthDebounced] = useState(width);

    const length = items.length;
    const spacing = 12;
    const lines = height > 300 ? 4 : 3;
    const maxPictureWidth = (height - (lines - 1) * spacing) / lines;
    const columns = Math.ceil(
      (widthDebounced + spacing) / (maxPictureWidth + spacing)
    );
    const imageSize = (widthDebounced + spacing) / columns - spacing;
    const pageHeight = (imageSize + spacing) * lines - spacing;
    const limit = columns * lines;
    const showPagination = length > limit;

    const { page, onPageUp, onPageDown, onPageReset } = usePages(length, limit);

    const handleScrollDown = useCallback(() => {
      setScroll((prevScroll) => prevScroll - (pageHeight + spacing));
      return onPageUp();
    }, [onPageUp, pageHeight]);

    const handleScrollUp = useCallback(() => {
      setScroll((prevScroll) => prevScroll + (pageHeight + spacing));
      return onPageDown();
    }, [onPageDown, pageHeight]);

    useDebounce(() => setWidthDebounced(width), 300, [width]);

    useEffect(() => {
      setScroll(0);
      onPageReset();
    }, [onPageReset, widthDebounced]);

    const highlightedImage = useMemo(
      () =>
        hovered && (
          <Highlight
            size={imageSize}
            top={hovered.top + scroll + (height - pageHeight) / 2}
            left={hovered.left}
            image={hovered.src}
          />
        ),
      [height, hovered, imageSize, pageHeight, scroll]
    );

    // generate images only if we need to display them
    useEffect(() => {
      if (displayLimit < page.to) {
        setDisplayLimit(page.to);
      }
    }, [page, displayLimit]);

    const displayedPics = useMemo(
      () => slice(items, 0, displayLimit),
      [displayLimit, items]
    );

    const picturesRenderer = useMemo(
      () => (
        <>
          {map(displayedPics, (item: Item, i: number) => {
            const y = Math.floor(i / columns);
            const top = y * (imageSize + spacing);
            const left = (i - y * columns) * (imageSize + spacing);
            return (
              <Tooltip
                tooltip={renderTooltip(item.data)}
                key={getItemUID(item)}
              >
                <PicPosition style={{ top, left }}>
                  <PictureLoader
                    imageSize={imageSize}
                    item={item}
                    onHoverImage={(image: string) => {
                      setHovered(image ? { top, left, src: image } : null);
                    }}
                    setInitialized={setInitialized}
                  />
                </PicPosition>
              </Tooltip>
            );
          })}
        </>
      ),
      [columns, displayedPics, imageSize, renderTooltip]
    );
    return (
      <Root className={className}>
        <Body ref={ref}>
          {highlightedImage}
          <Wrapper
            initialized={initialized}
            style={{ height: pageHeight, marginTop: (height - pageHeight) / 2 }}
          >
            <Pics style={{ transform: `translateY(${scroll}px)` }}>
              {picturesRenderer}
            </Pics>
          </Wrapper>
        </Body>
        <Footer>
          {onClick && (
            <Button
              variant="tertiary"
              text={buttonLabel}
              onClick={onClick}
              isDisabled={!onClick}
            />
          )}
          {showPagination && (
            <Pagination className={className}>
              <Button
                onClick={handleScrollDown}
                icon="chevron-small-down"
                isDisabled={page.to >= length}
                variant="tertiary"
                size="s"
              />
              <Button
                onClick={handleScrollUp}
                icon="chevron-small-up"
                isDisabled={page.from === 1}
                variant="tertiary"
                size="s"
              />
              <Counter>
                {page.from} - {page.to}
              </Counter>
            </Pagination>
          )}
        </Footer>
      </Root>
    );
  }
);

export default PictureWall;
